import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Badge } from '../components/ui/badge';
import { 
  Calculator, PoundSterling, ArrowRight, CheckCircle2, 
  FileText, HelpCircle, TrendingUp, Globe, Gift
} from 'lucide-react';
import { motion } from 'framer-motion';
import CryptoTaxCalculatorTool from '../components/tools/stage1/CryptoTaxCalculatorTool';
import { TaxPageMonetization, TaxReportEmailCapture } from '../components/TaxPageMonetization';

const CryptoTaxCalculatorUK = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How is cryptocurrency taxed in the UK?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "In the UK, cryptocurrency is subject to Capital Gains Tax (CGT) when you dispose of it. Basic rate taxpayers pay 10% CGT, while higher and additional rate taxpayers pay 20%. You have a tax-free allowance of £3,000 per year (2026/27)."
        }
      },
      {
        "@type": "Question",
        "name": "What is the UK crypto tax-free allowance for 2026?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The Capital Gains Tax annual exempt amount for 2026/27 is £3,000. This means the first £3,000 of your crypto gains each tax year are tax-free. Any gains above this threshold are taxed at 10% or 20%."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need to report crypto to HMRC?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, you must report crypto disposals to HMRC if your total gains exceed the annual exempt amount, or if the total value of crypto disposed of exceeds 4 times the allowance. Use the Capital Gains Tax service or Self Assessment tax return."
        }
      },
      {
        "@type": "Question",
        "name": "Is staking crypto taxable in the UK?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, crypto staking rewards are typically taxed as miscellaneous income at your income tax rate. When you later sell the staked tokens, any gain is subject to Capital Gains Tax."
        }
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="Crypto Tax Calculator UK (2026) - Free HMRC Capital Gains Calculator"
        description="Calculate UK cryptocurrency taxes free. Estimate Capital Gains Tax on Bitcoin, Ethereum with 2026/27 CGT rates. HMRC-compliant calculator with £3,000 annual allowance."
        keywords="crypto tax calculator UK, UK cryptocurrency tax, HMRC crypto tax 2026, Bitcoin tax UK, capital gains tax crypto UK, CGT calculator crypto"
        canonical="https://toolsforgeapp.com/crypto-tax-calculator-uk"
      />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <div className="min-h-screen py-12 px-6" data-testid="crypto-tax-uk-page">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <span>/</span>
            <Link to="/crypto-calculators" className="hover:text-primary transition-colors">Crypto Calculators</Link>
            <span>/</span>
            <span className="text-foreground">UK Tax Calculator</span>
          </nav>

          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <Badge variant="outline" className="mb-4 px-4 py-1.5 border-blue-500/30 text-blue-400">
              <Globe className="w-3 h-3 mr-2" />
              Updated for 2026/27 Tax Year
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 font-['Plus_Jakarta_Sans']">
              Crypto Tax Calculator UK
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Calculate your UK Capital Gains Tax on cryptocurrency disposals with our free HMRC-compliant calculator. Instantly estimate CGT on Bitcoin, Ethereum, and all crypto assets using the latest 2026/27 tax rates and the £3,000 annual exempt amount. Perfect for UK taxpayers managing their crypto portfolio.
            </p>
          </motion.div>

          {/* Key Info Cards */}
          <div className="grid md:grid-cols-3 gap-4 mb-10">
            <div className="p-4 rounded-xl border border-blue-500/30 bg-blue-500/10">
              <div className="flex items-center gap-2 mb-2">
                <PoundSterling className="w-5 h-5 text-blue-400" />
                <span className="font-semibold">Basic Rate</span>
              </div>
              <p className="text-2xl font-bold text-blue-400">10%</p>
              <p className="text-xs text-muted-foreground">Income under £50,270</p>
            </div>
            <div className="p-4 rounded-xl border border-purple-500/30 bg-purple-500/10">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-purple-400" />
                <span className="font-semibold">Higher Rate</span>
              </div>
              <p className="text-2xl font-bold text-purple-400">20%</p>
              <p className="text-xs text-muted-foreground">Income over £50,270</p>
            </div>
            <div className="p-4 rounded-xl border border-green-500/30 bg-green-500/10">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="w-5 h-5 text-green-400" />
                <span className="font-semibold">Tax-Free Allowance</span>
              </div>
              <p className="text-2xl font-bold text-green-400">£3,000</p>
              <p className="text-xs text-muted-foreground">Annual exempt amount</p>
            </div>
          </div>

          {/* Calculator Tool */}
          <div className="mb-8">
            <CryptoTaxCalculatorTool />
          </div>

          {/* Monetization Section - After Calculator */}
          <TaxPageMonetization 
            region="uk"
            complianceText="Stay compliant with HMRC Capital Gains Tax regulations"
          />

          {/* How to Calculate Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <Calculator className="w-6 h-6 text-primary" />
              How to Calculate Crypto Tax in the UK
            </h2>
            
            <div className="prose prose-invert max-w-none">
              <p className="text-muted-foreground mb-4">
                HMRC treats cryptocurrency as a chargeable asset for Capital Gains Tax purposes. When you dispose of crypto (sell, trade, gift, or spend), you may owe CGT. Here's the process:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm">1</span>
                    Calculate Your Cost Basis
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Add up your purchase price plus any allowable costs (exchange fees, gas fees). HMRC accepts FIFO, average cost, or specific identification methods.
                  </p>
                </div>
                
                <div className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm">2</span>
                    Determine Disposal Value
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Record the GBP value at the time of disposal. For crypto-to-crypto trades, use the sterling value of the crypto you received.
                  </p>
                </div>
                
                <div className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm">3</span>
                    Apply Annual Exempt Amount
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Subtract the £3,000 tax-free allowance from your total gains. Only gains exceeding this threshold are taxable.
                  </p>
                </div>
                
                <div className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm">4</span>
                    Apply CGT Rate
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Pay 10% if you're a basic rate taxpayer, or 20% if you're a higher/additional rate taxpayer. Your income band determines the rate.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Example Calculation */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12 p-6 rounded-xl border border-border bg-card"
          >
            <h2 className="text-xl font-bold mb-4 font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-400" />
              Example: UK Crypto Tax Calculation
            </h2>
            
            <div className="bg-gray-900/50 rounded-lg p-4 font-mono text-sm">
              <p className="text-muted-foreground mb-2">// Scenario: Higher rate taxpayer selling Ethereum</p>
              <p className="text-blue-400">Purchase Price: £2,000</p>
              <p className="text-blue-400">Sale Price: £8,500</p>
              <p className="text-blue-400">Quantity: 2 ETH</p>
              <p className="text-muted-foreground mt-2">// Calculation</p>
              <p className="text-white">Total Cost: 2 × £2,000 = <span className="text-yellow-400">£4,000</span></p>
              <p className="text-white">Total Proceeds: 2 × £8,500 = <span className="text-yellow-400">£17,000</span></p>
              <p className="text-white">Capital Gain: £17,000 - £4,000 = <span className="text-green-400">£13,000</span></p>
              <p className="text-white">Less Annual Exemption: £13,000 - £3,000 = <span className="text-yellow-400">£10,000</span></p>
              <p className="text-white">Tax Rate (Higher rate): <span className="text-yellow-400">20%</span></p>
              <p className="text-white mt-2 text-lg">Capital Gains Tax Owed: £10,000 × 20% = <span className="text-red-400 font-bold">£2,000</span></p>
            </div>
          </motion.section>

          {/* Email Capture - Before FAQ */}
          <TaxReportEmailCapture region="uk" />

          {/* FAQ Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-purple-400" />
              UK Crypto Tax FAQs
            </h2>
            
            <div className="space-y-4">
              {[
                {
                  q: "How is cryptocurrency taxed in the UK?",
                  a: "HMRC treats crypto as a chargeable asset subject to Capital Gains Tax. When you dispose of crypto, you pay 10% CGT if you're a basic rate taxpayer or 20% if you're a higher/additional rate taxpayer. The first £3,000 of gains each year are tax-free."
                },
                {
                  q: "What is the UK crypto tax-free allowance for 2026?",
                  a: "The Capital Gains Tax annual exempt amount for 2026/27 is £3,000. This has been significantly reduced from previous years (was £12,300 until 2023). Only gains exceeding this threshold are subject to CGT."
                },
                {
                  q: "Do I need to report crypto to HMRC?",
                  a: "Yes, if your total crypto gains exceed £3,000, or if total disposal proceeds exceed £12,000, you must report to HMRC. Use the Capital Gains Tax service for real-time reporting or include in your Self Assessment tax return."
                },
                {
                  q: "Is staking crypto taxable in the UK?",
                  a: "Staking rewards are typically treated as miscellaneous income and taxed at your income tax rate when received. When you later sell the staked tokens, any gain above your original value is subject to Capital Gains Tax."
                },
                {
                  q: "Can I offset crypto losses in the UK?",
                  a: "Yes, capital losses can offset capital gains in the same tax year. Unused losses can be carried forward indefinitely. You must report losses to HMRC within 4 years to claim them."
                }
              ].map((faq, index) => (
                <div key={index} className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    {faq.q}
                  </h3>
                  <p className="text-sm text-muted-foreground">{faq.a}</p>
                </div>
              ))}
            </div>
          </motion.section>

          {/* Related Tools */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="text-xl font-semibold mb-4 font-['Plus_Jakarta_Sans']">Related Calculators</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { name: 'Bitcoin Profit Calculator', slug: 'bitcoin-profit-calculator' },
                { name: 'Ethereum Calculator', slug: 'ethereum-profit-calculator' },
                { name: 'Crypto ROI Calculator', slug: 'crypto-roi-calculator' },
                { name: 'All Crypto Tools', slug: '../crypto-calculators', isHub: true }
              ].map(tool => (
                <Link
                  key={tool.slug}
                  to={tool.isHub ? '/crypto-calculators' : `/tools/${tool.slug}`}
                  className="p-3 rounded-lg border border-border bg-card hover:border-primary/50 transition-all text-sm font-medium flex items-center justify-between"
                >
                  {tool.name}
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </motion.section>
        </div>
      </div>
    </>
  );
};

export default CryptoTaxCalculatorUK;
